/**
 * Bestimmt die Electron-Session-Partition eines Browser-Panels.
 *
 * WARUM ES DIESE DATEI GIBT (Vorfall 19.08.2026)
 * ----------------------------------------------
 * Die Partition eines isolierten Panels hing allein an der Panel-ID:
 * `persist:browser-<panelId>`. Die Panel-ID ist aber die ID eines
 * Layout-Knotens — die Layout-Automatik hat sie an einem Tag zweimal neu
 * vergeben. Folge: neuer, leerer Cookie-Jar, die eingeloggte Sitzung schien
 * verloren, und die alte Partition lag nur noch unter einer verwaisten UUID
 * auf der Platte. Wiederherstellung ging nur, indem man die alte ID von Hand
 * ins Layout zurückschrieb.
 *
 * Eine Anmeldung darf nicht an einer ID hängen, die die UI neu vergeben kann.
 * Panels, deren Sitzung überleben soll, bekommen darum einen STABILEN NAMEN
 * aus der Layout-Config (`"partition": "linkedin"`), der von der Knoten-ID
 * entkoppelt ist. Der Layout-Knoten darf sich beliebig ändern.
 *
 * FAIL LOUD: Jede unklare Eingabe wirft. Ein stiller Rückfall auf die
 * geteilte Partition ist hier die gefährlichste Variante — er sieht aus wie
 * "funktioniert", meldet aber alle Panels an derselben Sitzung an und kostet
 * im anderen Fall unbemerkt eine Anmeldung. Jedes Panel steckt in einer
 * ErrorBoundary (siehe LayoutManager.wrapPanel), ein Wurf ist also sichtbar,
 * ohne die App zu reissen.
 */

/**
 * Präfix für benannte Partitionen. Das `named-` dazwischen ist Absicht: es
 * trennt den Namensraum hart von den Alt-Partitionen `persist:browser-<uuid>`.
 * Ohne diese Trennung würde ein Name, der zufällig wie eine alte Panel-ID
 * aussieht, deren Cookie-Jar stillschweigend übernehmen.
 */
export const NAMED_PARTITION_PREFIX = 'persist:browser-named-';

/** Rafaels normaler, dauerhafter Arbeitsbrowser für alle echten Dienste. */
export const RAFAEL_STANDARD_PROFILE = 'rafael-standard';

/**
 * Alle nicht-isolierten Browser-Panels teilen Rafaels festen Standardbrowser.
 * Der alte Wert persist:browser bleibt als historischer Cookie-Jar auf der Platte liegen,
 * wird aber nie still wiederverwendet.
 */
export const SHARED_PARTITION = `${NAMED_PARTITION_PREFIX}${RAFAEL_STANDARD_PROFILE}`;

/**
 * Erlaubte Partitionsnamen: Kleinbuchstaben, Ziffern, Bindestrich; Beginn
 * alphanumerisch; höchstens 63 Zeichen. Bewusst eng — der Wert landet in einem
 * Electron-Partitionsstring und darüber in einem Verzeichnisnamen auf der Platte.
 */
export const PARTITION_NAME_PATTERN = /^[a-z0-9][a-z0-9-]{0,62}$/;

export interface ResolveBrowserPartitionInput {
  /**
   * Stabiler Name aus der Layout-Config (`config.partition`). Kommt aus JSON,
   * ist deshalb `unknown` und wird hier geprüft statt vertraut.
   * `undefined`, `null` und `''` heissen "nicht konfiguriert" — das ist der
   * Normalzustand jedes Panels, kein Fehler.
   */
  partitionName?: unknown;
  /** Alt-Weg: eigene Partition, an die Panel-ID gebunden. */
  isolated?: boolean;
  /** ID des Layout-Knotens. Für `isolated` zwingend. */
  panelId?: string;
}

/**
 * Liefert den Partitionsstring für das `<webview partition="...">`.
 *
 * Reihenfolge:
 *   1. `partitionName` gesetzt  → `persist:browser-named-<name>`  (überlebt ID-Wechsel)
 *   2. `isolated`               → `persist:browser-<panelId>`     (Alt-Verhalten)
 *   3. sonst                    → `persist:browser-named-rafael-standard`
 *
 * `partitionName` und `isolated` schliessen sich nicht aus: "isoliert, und zwar
 * unter diesem Namen" ist die erwünschte Kombination. Der Name gewinnt.
 *
 * @throws {Error} bei gesetztem, aber unbrauchbarem `partitionName`
 * @throws {Error} bei `isolated` ohne `panelId`
 */
export function resolveBrowserPartition(input: ResolveBrowserPartitionInput): string {
  const { partitionName, isolated = false, panelId } = input;

  const nameConfigured = partitionName !== undefined && partitionName !== null && partitionName !== '';

  if (nameConfigured) {
    if (typeof partitionName !== 'string') {
      throw new Error(
        `[browserPartition] config.partition muss ein String sein, ist aber ${typeof partitionName} ` +
        `(${JSON.stringify(partitionName)}). Beispiel: "partition": "linkedin".`,
      );
    }
    if (!PARTITION_NAME_PATTERN.test(partitionName)) {
      const vorschlag = partitionName.toLowerCase().replace(/[^a-z0-9-]/g, '-').replace(/^-+/, '');
      throw new Error(
        `[browserPartition] Ungültiger Partitionsname ${JSON.stringify(partitionName)}. ` +
        `Erlaubt sind Kleinbuchstaben, Ziffern und Bindestriche, Beginn alphanumerisch, max. 63 Zeichen. ` +
        (PARTITION_NAME_PATTERN.test(vorschlag) ? `Gemeint war vermutlich "${vorschlag}".` : ''),
      );
    }
    return `${NAMED_PARTITION_PREFIX}${partitionName}`;
  }

  if (isolated) {
    // Früher fiel dieser Fall still auf die geteilte Partition zurück. Genau das
    // ist der gefährliche Ausgang: Das Panel hat Isolation ANGEFORDERT und bekäme
    // stattdessen die Sitzung aller anderen Panels — sichtbar wird das erst, wenn
    // sich zwei Anmeldungen gegenseitig überschreiben.
    if (typeof panelId !== 'string' || panelId === '') {
      throw new Error(
        '[browserPartition] Panel verlangt Isolation (isolated=true), hat aber keine Panel-ID. ' +
        'Ohne stabilen Schlüssel gibt es keine eigene Sitzung. Entweder eine Panel-ID durchreichen ' +
        'oder in der Layout-Config einen festen Namen setzen: "partition": "<name>".',
      );
    }
    return 'persist:browser-' + panelId;
  }

  return SHARED_PARTITION;
}
